import { toDocument } from '@littlebird/action-cards';
import {
  Extension,
  generateText,
  textblockTypeInputRule,
  type Editor,
  type JSONContent,
} from '@tiptap/core';
import CodeBlock from '@tiptap/extension-code-block';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import History from '@tiptap/extension-history';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import Paragraph from '@tiptap/extension-paragraph';
import { TrailingNode } from '@tiptap/extensions';
import { Slice } from '@tiptap/pm/model';
import { EditorState, Plugin } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { useEditor } from '@tiptap/react';
import { isEqual } from 'lodash';
import {
  type MutableRefObject,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import {
  Bold,
  BulletList,
  Code,
  Heading,
  Italic,
  LinkHoverExtension,
  MarkdownExtension,
  OrderedList,
  Strike,
} from '@/components/ui/editor/extensions';
import {
  EntityReference,
  entityReferenceDocument,
  EntityReferenceText,
} from '@/components/ui/editor/extensions/entity-reference';
import { SearchAndReplace } from '@/components/ui/editor/extensions/search-and-replace';
import {
  collectMentions,
  ComposerDraftContext,
  type MentionItem,
} from '@/features/mention';
import { logChatRenderDebug } from '@/utils/chat-render-debug';
import { classList } from '@/utils/cn';

import {
  emojiExtension,
  mentionExtensions,
  skillExtension,
} from './composer-mention';
import { modEnterSends } from './useRegisterSendChat';

export type ComposerPasteEvent = {
  clipboardData: DataTransfer | null;
  preventDefault: () => void;
};

export type ComposerEditorSelection = {
  from: number;
  to: number;
  createTextInsertion: (
    text: string,
    sourceDocument?: JSONContent,
    sourceText?: string
  ) => {
    content: string;
    document: JSONContent;
    insertedText: string;
  } | null;
};

export type ComposerEditorHandle = {
  getSelection: () => ComposerEditorSelection;
  insertTextAtSelection: (
    text: string,
    selection: ComposerEditorSelection
  ) => boolean;
  focusAtEnd: () => void;
};

// The send key belongs to `chat.send`; newline keys use editor block semantics.
// Touch keyboards have no Shift-Enter, so Enter inserts the newline there and
// sending falls to the button (a hardware Mod-Enter still sends).
const ComposerKeymap = Extension.create({
  name: 'composerKeymap',
  priority: 110,
  addKeyboardShortcuts() {
    const insertNewline = () =>
      this.editor.commands.first(({ commands }) => [
        () => commands.splitListItem('listItem'),
        () => commands.newlineInCode(),
        () => commands.createParagraphNear(),
        () => commands.liftEmptyBlock(),
        () => commands.splitBlock(),
      ]);
    /* eslint-disable littlebird/naming-convention -- TipTap shortcut keys use keyboard names */
    return {
      Enter: () => (modEnterSends() ? insertNewline() : true),
      'Mod-Enter': () => true,
      'Shift-Enter': insertNewline,
    };
  },
});

const ComposerMarkdownExtension = MarkdownExtension.configure({
  transformCopiedText: false,
  transformPastedText: false,
});

const getComposerContent = (editor: Editor) => {
  return (
    editor.storage as unknown as {
      markdown: { getMarkdown(): string };
    }
  ).markdown.getMarkdown();
};

export type ComposerEditorOptions = {
  value: string;
  /** Restores rich text exactly; without it, value is treated as literal text. */
  document?: JSONContent;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Full-height surface: content pads from the top instead of hugging one line */
  fill?: boolean;
  /** Enter inserts a line break instead of being left to the send hotkey */
  multiline?: boolean;
  find?: boolean;
  ariaLabel?: string;
  onChange: (text: string, document: JSONContent) => void;
  onPaste: (event: ComposerPasteEvent) => boolean;
  inputRef: (element: HTMLElement | null) => void;
  editorHandleRef?: MutableRefObject<ComposerEditorHandle | null>;
  /** Kept in sync with structured mentions for the send path to read. */
  mentionsRef?: MutableRefObject<MentionItem[]>;
  draftId?: string;
  /** Mod-Enter's own action, such as sending a docked draft from an empty box; false leaves it to the send hotkey */
  onModEnter?: () => boolean;
};

export const useComposerEditor = ({
  value,
  document,
  disabled,
  autoFocus,
  fill,
  multiline,
  find,
  ariaLabel,
  onChange,
  onPaste,
  inputRef,
  editorHandleRef,
  mentionsRef,
  draftId,
  onModEnter,
}: ComposerEditorOptions) => {
  const callbacks = useRef({
    onChange,
    onPaste,
    mentionsRef,
    draftId,
    onModEnter,
  });
  callbacks.current = { onChange, onPaste, mentionsRef, draftId, onModEnter };
  const locallyEmittedDocument = useRef<JSONContent | null>(null);
  const restoredEditor = useRef<Editor | null>(null);

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      EntityReferenceText,
      HardBreak.extend({
        addStorage() {
          return {
            ...this.parent?.(),
            markdown: {
              // Chat Markdown preserves single newlines, including pasted table rows.
              serialize(state: { write(text: string): void }) {
                state.write('\n');
              },
            },
          };
        },
      }),
      History,
      Extension.create({
        name: 'composerModEnter',
        priority: 120,
        addKeyboardShortcuts: () => ({
          // eslint-disable-next-line littlebird/naming-convention -- TipTap shortcut keys use keyboard names
          'Mod-Enter': () => callbacks.current.onModEnter?.() ?? false,
        }),
      }),
      TrailingNode.configure({
        notAfter: ['heading', 'bulletList', 'orderedList'],
      }),
      Bold,
      Italic,
      Strike,
      Code.extend({
        addProseMirrorPlugins() {
          return [
            new Plugin({
              props: {
                decorations: (state) => {
                  const { empty, $from } = state.selection;
                  if (
                    !empty ||
                    !this.type.isInSet($from.nodeBefore?.marks ?? []) ||
                    this.type.isInSet(state.storedMarks ?? $from.marks())
                  )
                    return null;
                  // A view-only caret anchor outside code; never stored in the draft.
                  return DecorationSet.create(state.doc, [
                    Decoration.widget(
                      $from.pos,
                      (view) => {
                        const span =
                          view.dom.ownerDocument.createElement('span');
                        span.dataset.codeCaret = '';
                        span.textContent = '\u200b';
                        return span;
                      },
                      { side: -1, marks: [] }
                    ),
                  ]);
                },
              },
            }),
          ];
        },
      }).configure({
        HTMLAttributes: {
          spellcheck: 'false',
          class: 'rounded-sm bg-surface-active px-1 font-mono font-normal',
        },
      }),
      CodeBlock.extend({
        addInputRules() {
          return [
            ...(this.parent?.() ?? []),
            textblockTypeInputRule({ find: /^```$/, type: this.type }),
          ];
        },
      }).configure({
        HTMLAttributes: {
          spellcheck: 'false',
          class:
            'my-2 rounded-md border border-border-strong bg-surface-active p-2 font-mono text-sm whitespace-pre-wrap',
        },
      }),
      Heading,
      ListItem,
      BulletList.configure({
        HTMLAttributes: { class: 'list-disc list-outside pl-5' },
      }),
      OrderedList.configure({
        HTMLAttributes: { class: 'list-decimal list-outside pl-5' },
      }),
      // TipTap ties inclusive to autolink; that would underline text typed after a URL.
      Link.extend({ inclusive: false }).configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-muted-foreground underline underline-offset-[3px]',
        },
      }),
      LinkHoverExtension,
      ComposerMarkdownExtension,
      ...(find ? [SearchAndReplace] : []),
      ...(!multiline ? [ComposerKeymap] : []),
      ...mentionExtensions,
      EntityReference,
      ComposerDraftContext.configure({
        getDraftId: () => callbacks.current.draftId,
      }),
      skillExtension,
      emojiExtension,
    ],
    content: entityReferenceDocument(value, document),
    autofocus: autoFocus ? 'end' : false,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        'data-testid': 'chat-input',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        class: classList(
          'w-full outline-none whitespace-pre-wrap break-words [&_p]:m-0',
          '[&_h1]:m-0 [&_h1]:text-lg [&_h1]:font-semibold',
          '[&_h2]:m-0 [&_h2]:text-base [&_h2]:font-semibold',
          '[&_h3]:m-0 [&_h3]:text-sm [&_h3]:font-semibold',
          fill ? 'pt-3' : 'py-0.5'
        ),
      },
      handlePaste: (view, event) => {
        if (callbacks.current.onPaste(event)) return true;

        const clipboardData = event.clipboardData;
        const html = clipboardData?.getData('text/html');
        const text = clipboardData?.getData('text/plain');
        if (!text || !html?.toLowerCase().includes('<table')) return false;

        if (view.state.selection.$from.parent.type.spec.code)
          return view.pasteText(text);
        const document = view.state.schema.nodeFromJSON(toDocument(text));
        view.dispatch(
          view.state.tr
            .replaceSelection(Slice.maxOpen(document.content))
            .scrollIntoView()
        );
        return true;
      },
    },
    onCreate: ({ editor }) => {
      if (callbacks.current.mentionsRef)
        callbacks.current.mentionsRef.current = collectMentions(editor);
      if (document) {
        const createdDocument = editor.getJSON();
        locallyEmittedDocument.current = createdDocument;
        callbacks.current.onChange(getComposerContent(editor), createdDocument);
      }
    },
    onUpdate: ({ editor }) => {
      const content = getComposerContent(editor);
      const currentDocument = editor.getJSON();
      locallyEmittedDocument.current = currentDocument;
      callbacks.current.onChange(content, currentDocument);
      const mentions = callbacks.current.mentionsRef
        ? collectMentions(editor)
        : undefined;
      if (callbacks.current.mentionsRef)
        callbacks.current.mentionsRef.current = mentions ?? [];
      requestAnimationFrame(() => editor.commands.scrollIntoView());
    },
  });

  useEffect(() => {
    inputRef(editor?.view.dom ?? null);
    if (editorHandleRef) {
      editorHandleRef.current = editor
        ? {
            focusAtEnd: () =>
              editor.commands.focus('end', { scrollIntoView: false }),
            getSelection: () => {
              const { state } = editor;
              const { from, to } = state.selection;
              const extensions = editor.extensionManager.extensions;
              return {
                from,
                to,
                createTextInsertion: (text, sourceDocument, sourceText) => {
                  const currentDocument =
                    sourceDocument ??
                    (sourceText === undefined
                      ? undefined
                      : toDocument(sourceText));
                  const insertionState = currentDocument
                    ? EditorState.create({
                        doc: state.schema.nodeFromJSON(currentDocument),
                      })
                    : state;
                  const maxPosition = currentDocument
                    ? Math.max(1, insertionState.doc.content.size - 1)
                    : insertionState.doc.content.size;
                  const insertionFrom = currentDocument
                    ? Math.min(from, maxPosition)
                    : from;
                  const insertionTo = currentDocument ? insertionFrom : to;
                  if (
                    insertionFrom < 0 ||
                    insertionTo < insertionFrom ||
                    insertionTo > insertionState.doc.content.size
                  ) {
                    return null;
                  }
                  const resolvedFrom =
                    insertionState.doc.resolve(insertionFrom);
                  const resolvedTo = insertionState.doc.resolve(insertionTo);
                  const nodeBefore = resolvedFrom.nodeBefore;
                  const nodeAfter = resolvedTo.nodeAfter;
                  const needsLeadingSpace = Boolean(
                    nodeBefore &&
                    !/^[\s,.;:!?…\)\]}"”’]/.test(text) &&
                    (nodeBefore.isText
                      ? !/[\s\(\[{"“‘]$/.test(nodeBefore.text ?? '')
                      : nodeBefore.type.name !== 'hardBreak')
                  );
                  const needsTrailingSpace = Boolean(
                    nodeAfter &&
                    !/\s$/.test(text) &&
                    (nodeAfter.isText
                      ? !/^[\s,.;:!?…\)\]}"”’]/.test(nodeAfter.text ?? '')
                      : nodeAfter.type.name !== 'hardBreak')
                  );
                  const insertedText = `${needsLeadingSpace ? ' ' : ''}${text}${
                    needsTrailingSpace ? ' ' : ''
                  }`;
                  const insertionDocument = insertionState.tr
                    .insertText(insertedText, insertionFrom, insertionTo)
                    .doc.toJSON();

                  return {
                    content: generateText(insertionDocument, extensions, {
                      blockSeparator: '\n',
                    }),
                    document: insertionDocument,
                    insertedText,
                  };
                },
              };
            },
            insertTextAtSelection: (text, selection) => {
              const insertion = selection.createTextInsertion(text);
              if (!insertion) return false;

              return editor.commands.insertContentAt(
                selection,
                {
                  type: 'text',
                  text: insertion.insertedText,
                },
                { updateSelection: true }
              );
            },
          }
        : null;
    }
    if (mentionsRef)
      mentionsRef.current = editor ? collectMentions(editor) : [];
    return () => {
      inputRef(null);
      if (editorHandleRef) editorHandleRef.current = null;
      if (mentionsRef) mentionsRef.current = [];
    };
  }, [editor, editorHandleRef, inputRef, mentionsRef]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  useLayoutEffect(() => {
    if (!editor) return;
    let contentMatches = false;
    let didSetContent = false;
    const isLocalFeedback =
      document !== undefined && document === locallyEmittedDocument.current;
    if (isLocalFeedback) {
      locallyEmittedDocument.current = null;
      contentMatches = true;
    } else if (document) {
      const currentDocument = editor.getJSON();
      contentMatches = isEqual(currentDocument, document);
    } else if (restoredEditor.current === editor) {
      contentMatches = getComposerContent(editor) === value;
    }
    if (!contentMatches) {
      didSetContent = true;
      restoredEditor.current = editor;
      editor.commands.setContent(entityReferenceDocument(value, document), {
        emitUpdate: false,
      });
      if (mentionsRef) mentionsRef.current = collectMentions(editor);
      if (document) {
        const synchronizedDocument = editor.getJSON();
        locallyEmittedDocument.current = synchronizedDocument;
        callbacks.current.onChange(
          getComposerContent(editor),
          synchronizedDocument
        );
      }
      if (editor.isFocused)
        editor.commands.focus('end', { scrollIntoView: false });
    }
    logChatRenderDebug('composer-draft-sync', {
      isLocalFeedback,
      contentMatches,
      hasDocument: document !== undefined,
      didSetContent,
      valueLength: value.length,
    });
  }, [document, editor, value, mentionsRef]);

  return editor;
};
